import type { WidgetField, WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";

import { isEhrRegistryName } from "./ehrUrl";

/** Any source the extractor produced — i.e. everything the user did not type. */
type MachineSource = Exclude<WidgetFieldSource, { kind: "user" }>;

/** Each distinct document backing the value, in the order it was reported. */
function documentFilenames(matching: MachineSource[]): string[] {
  const filenames: string[] = [];
  for (const candidate of matching) {
    if (candidate.kind === "ehr" || filenames.includes(candidate.filename)) continue;
    filenames.push(candidate.filename);
  }
  return filenames;
}

/**
 * The provenance a `FieldRow` credits under its value: the first source that
 * reported it, plus every other document that reported the same value —
 * naming one of three files that agree would understate the finding, so the
 * strip carries them all and shows the rest as "+n".
 *
 * No page is credited. A finding row carries one `reasoning` string that the
 * widget mapper copies onto every one of its `source_filenames`, so the page
 * scraped out of it named whichever file the strip happened to lead with. It
 * comes back when the extractor persists per-document evidence.
 */
export function sourceReference(field: WidgetField): {
  /** `registry` = an EHR row whose source system is some other registry. */
  kind: "ehr" | "registry" | "extractor" | "ifc";
  filename: string;
  /** Every document backing this value, first included. Empty for a registry. */
  filenames: string[];
} | null {
  const matching = field.sources.filter(
    (candidate): candidate is MachineSource =>
      candidate.kind !== "user" &&
      (field.state === "empty" || candidate.value.trim() === field.value.trim())
  );
  const source = matching[0];
  if (!source) return null;

  if (source.kind === "ehr") {
    return {
      kind: isEhrRegistryName(source.sourceSystem) ? "ehr" : "registry",
      filename: source.sourceSystem,
      filenames: [],
    };
  }

  return {
    kind: source.kind,
    filename: source.filename,
    filenames: documentFilenames(matching),
  };
}
